import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import * as playsApi from '../services/playsApi'

const usePlayStore = create(
  subscribeWithSelector((set, get) => ({
    // Play data
    currentPlay: {
      id: null,
      name: 'Untitled Play',
      description: '',
      isPublic: false,
      sportType: 'soccer', // Default sport type
      tags: [], // Array of tag strings
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    
    // Track original loaded play to detect name changes
    originalLoadedPlay: {
      id: null,
      name: null
    },
    
    // Teams on field
    teams: [],
    
    // Props on field
    props: [],
    
    // Animation data
    animations: [],
    isPlaying: false,
    currentTime: 0,
    duration: 10, // seconds
    
    // Selected entity for highlighting
    selectedEntity: null, // { type: 'team' | 'prop', id: string }
    
    // Field settings
    fieldSettings: {
      showGrid: true,
      showNumbers: true,
      zoom: 1, // Will be set by fit to screen
      panX: 0,
      panY: 0,
      fieldType: 'full', // 'full', 'half', 'plain'
    },
    
    // Actions
    setCurrentPlay: (play) => set({ currentPlay: play }),
    
    resetPlayName: () => set((state) => ({
      currentPlay: {
        ...state.currentPlay,
        name: 'Untitled Play',
        description: '',
        tags: [],
        isPublic: false,
        sportType: 'soccer'
      },
      originalLoadedPlay: {
        id: null,
        name: null
      }
    })),
    
    setSelectedEntity: (entity) => set({ selectedEntity: entity }),
    clearSelectedEntity: () => set({ selectedEntity: null }),
    
    addTeam: (team) => set((state) => {
      // Find the lowest available number for this team type (starting from 1)
      const teamsOfSameType = state.teams.filter(t => t.type === team.type)
      const existingNumbers = teamsOfSameType.map(t => parseInt(t.number) || 0).filter(n => n > 0).sort((a, b) => a - b)
      
      let nextNumber = 1
      if (existingNumbers.length > 0) {
        // Find the first gap in the numbers, or use the next number after the max
        for (let i = 1; i <= 99; i++) {
          if (!existingNumbers.includes(i)) {
            nextNumber = i
            break
          }
        }
        // If no gap found (all numbers 1-99 are taken), use 1 (shouldn't happen in practice)
        if (nextNumber > 99) {
          nextNumber = 1
        }
      }
      
      const newTeam = {
        id: Date.now().toString(),
        type: team.type,
        number: nextNumber.toString(),
        x: team.x,
        y: team.y,
        color: team.color || '#3B82F6'
      }
      
      
      return {
        teams: [...state.teams, newTeam],
        selectedEntity: null // Clear any existing selection when adding new team
      }
    }),
    
    updateTeam: (id, updates) => set((state) => ({
      teams: state.teams.map(team => 
        team.id === id ? { ...team, ...updates } : team
      )
    })),
    
    removeTeam: (id) => set((state) => ({
      teams: state.teams.filter(team => team.id !== id)
    })),
    
    clearTeams: () => set({ teams: [] }),
    
    addProp: (prop) => set((state) => ({
      props: [...state.props, {
        id: Date.now().toString(),
        type: prop.type,
        x: prop.x,
        y: prop.y,
        color: prop.color || '#F97316',
        ...prop
      }],
      selectedEntity: null // Clear any existing selection when adding new prop
    })),
    
    updateProp: (id, updates) => set((state) => ({
      props: state.props.map(prop => 
        prop.id === id ? { ...prop, ...updates } : prop
      )
    })),
    
    removeProp: (id) => set((state) => ({
      props: state.props.filter(prop => prop.id !== id)
    })),
    
    clearProps: () => set({ props: [] }),
    
    addAnimation: (animation) => set((state) => ({
      animations: [...state.animations, {
        id: Date.now().toString(),
        teamId: animation.teamId,
        type: animation.type || 'move',
        startTime: animation.startTime,
        endTime: animation.endTime,
        startX: animation.startX,
        startY: animation.startY,
        endX: animation.endX,
        endY: animation.endY,
        path: animation.path || [],
        duration: animation.endTime - animation.startTime,
        ...animation
      }]
    })),
    
    updateAnimation: (id, updates) => set((state) => ({
      animations: state.animations.map(anim => 
        anim.id === id ? { ...anim, ...updates } : anim
      )
    })),
    
    removeAnimation: (id) => set((state) => ({
      animations: state.animations.filter(anim => anim.id !== id)
    })),
    
    clearAnimations: () => set({ animations: [] }),
    
    setPlaying: (isPlaying) => set({ isPlaying }),
    setCurrentTime: (time) => set({ currentTime: time }),
    setDuration: (duration) => set({ duration }),
    
    updateFieldSettings: (settings) => set((state) => ({
      fieldSettings: { ...state.fieldSettings, ...settings }
    })),
    
    // Save/load functionality
    savePlay: async (existingPlayId = null, isPublic = false) => {
      const state = get()
      
      // Check if name has changed from the original loaded play
      const nameHasChanged = state.originalLoadedPlay.id && 
                             state.originalLoadedPlay.name && 
                             state.currentPlay.name !== state.originalLoadedPlay.name
      
      // If name has changed, create a new record (don't use original ID)
      let playIdToUse = existingPlayId || state.currentPlay.id
      if (nameHasChanged && state.originalLoadedPlay.id) {
        // Name changed - create new record
        playIdToUse = null
      } else if (!nameHasChanged && state.originalLoadedPlay.id) {
        // Name unchanged - use original ID
        playIdToUse = state.originalLoadedPlay.id
      }
      
      // Restore teams to their original starting positions before saving
      const teamsWithOriginalPositions = state.teams.map(team => {
        // Find the first animation for this team to get the starting position
        const firstAnimation = state.animations
          .filter(anim => anim.teamId === team.id)
          .sort((a, b) => a.startTime - b.startTime)[0]
        
        if (firstAnimation) {
          // Use the starting position from the first animation
          return {
            ...team,
            x: firstAnimation.startX,
            y: firstAnimation.startY
          }
        }
        
        // If no animation, use current position
        return team
      })
      
      // Restore props to their original starting positions before saving
      const propsWithOriginalPositions = state.props.map(prop => {
        // Find the first animation for this prop to get the starting position
        const firstAnimation = state.animations
          .filter(anim => anim.propId === prop.id)
          .sort((a, b) => a.startTime - b.startTime)[0]
        
        if (firstAnimation) {
          // Use the starting position from the first animation
          return {
            ...prop,
            x: firstAnimation.startX,
            y: firstAnimation.startY
          }
        }
        
        // If no animation, use current position
        return prop
      })
      
      const playData = {
        ...state.currentPlay,
        id: playIdToUse,
        teams: teamsWithOriginalPositions,
        props: propsWithOriginalPositions,
        animations: state.animations,
        fieldSettings: state.fieldSettings,
        updatedAt: new Date().toISOString()
      }
      
      // If creating a new record (name changed), set new createdAt timestamp
      if (nameHasChanged) {
        playData.createdAt = new Date().toISOString()
      }
      
      try {
        // Save to DynamoDB
        const savedPlay = await playsApi.savePlay(playData, isPublic)
        
        // Update originalLoadedPlay to reflect the new saved play
        set({ 
          currentPlay: savedPlay,
          originalLoadedPlay: {
            id: savedPlay.id,
            name: savedPlay.name
          }
        })
        return savedPlay
      } catch (error) {
        console.error('Error saving play:', error)
        alert('Failed to save play: ' + error.message)
        throw error
      }
    },
    
    loadPlay: async (playId, isUserOwnPlay = true) => {
      try {
        const play = await playsApi.loadPlay(playId, isUserOwnPlay)
        
        if (play) {
          const currentFieldSettings = get().fieldSettings
          const savedFieldSettings = play.fieldSettings || {}
          
          // First, clear all current content
          set({
            teams: [],
            props: [],
            animations: [],
            currentTime: 0,
            isPlaying: false
          })
          
          // Then, load the new play data
          set({
            currentPlay: play,
            originalLoadedPlay: {
              id: play.id,
              name: play.name
            },
            teams: play.teams || [],
            props: play.props || [],
            animations: play.animations || [],
            fieldSettings: {
              ...currentFieldSettings, // Keep current positioning settings
              fieldType: savedFieldSettings.fieldType !== undefined ? savedFieldSettings.fieldType : currentFieldSettings.fieldType, // Load saved field type
              showGrid: savedFieldSettings.showGrid !== undefined ? savedFieldSettings.showGrid : currentFieldSettings.showGrid,
              showNumbers: savedFieldSettings.showNumbers !== undefined ? savedFieldSettings.showNumbers : currentFieldSettings.showNumbers,
              zoom: undefined, // Clear zoom to force recalculation
              panX: undefined, // Clear pan X to force recalculation
              panY: undefined  // Clear pan Y to force recalculation
            }
          })
          
          // Trigger fit-to-screen after loading using direct method call
          setTimeout(() => {
            if (window.phaserScene && window.phaserScene.fitFieldToScreen) {
              window.phaserScene.fitFieldToScreen()
            }
          }, 100)
          return play
        }
        return null
      } catch (error) {
        console.error('Error loading play:', error)
        alert('Failed to load play: ' + error.message)
        return null
      }
    },
    
    getSavedPlays: async () => {
      try {
        return await playsApi.getUserPlays()
      } catch (error) {
        console.error('Error getting saved plays:', error)
        return []
      }
    },
    
    getPublicPlays: async () => {
      try {
        return await playsApi.getPublicPlays()
      } catch (error) {
        console.error('Error getting public plays:', error)
        return []
      }
    },
    
    deletePlay: async (playId) => {
      try {
        await playsApi.deletePlay(playId)
      } catch (error) {
        console.error('Error deleting play:', error)
        alert('Failed to delete play: ' + error.message)
        throw error
      }
    },
    
    // Import play from JSON data (for file import functionality)
    importPlay: (playData) => {
      const currentFieldSettings = get().fieldSettings
      const savedFieldSettings = playData.fieldSettings || {}
      
      // First, clear all current content
      set({
        teams: [],
        props: [],
        animations: [],
        currentTime: 0,
        isPlaying: false
      })
      
      // Then, load the imported play data
      set({
        currentPlay: {
          ...playData,
          // Ensure required fields exist
          id: playData.id || Date.now().toString(),
          name: playData.name || 'Imported Play',
          description: playData.description || '',
          sportType: playData.sportType || 'soccer',
          createdAt: playData.createdAt || new Date().toISOString(),
          updatedAt: playData.updatedAt || new Date().toISOString()
        },
        teams: playData.teams || [],
        props: playData.props || [],
        animations: playData.animations || [],
        fieldSettings: {
          ...currentFieldSettings,
          fieldType: savedFieldSettings.fieldType !== undefined 
            ? savedFieldSettings.fieldType 
            : currentFieldSettings.fieldType,
          showGrid: savedFieldSettings.showGrid !== undefined 
            ? savedFieldSettings.showGrid 
            : currentFieldSettings.showGrid,
          showNumbers: savedFieldSettings.showNumbers !== undefined 
            ? savedFieldSettings.showNumbers 
            : currentFieldSettings.showNumbers,
          // Clear zoom/pan to force recalculation
          zoom: undefined,
          panX: undefined,
          panY: undefined
        }
      })
      
      // Trigger fit-to-screen after importing
      setTimeout(() => {
        if (window.phaserScene && window.phaserScene.fitFieldToScreen) {
          window.phaserScene.fitFieldToScreen()
        }
      }, 100)
    }
  }))
)

export default usePlayStore
